<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue';

import {
  CabinTypeLabel,
  EngineTypeLabel,
  DrivetrainLabel,
  RoofTypeLabel,
  SizeTypeLabel,
  TechDataVariations,
  TechDataVehicle,
} from './types';
import { getDefaultLocaleValue } from './locales/default';

import { getPageEnvironmentVariables } from './utils/aem';

import TechDataSelector from '@/components/tech-data-selector.vue';
import TechDataSlider from './components/tech-data-slider.vue';
import TechDataStats from '@/components/tech-data-stats.vue';
import { loadTranslations } from './service/i18n';

import { useTracking } from '@/composables/useTracking';

const props = defineProps<{
  data: {
    aemData: {
      modelSeries: string;
      stage: string;
      language: string;
      country: string;
    };
    processedData: {
      sizes: SizeTypeLabel[];
      roofs: RoofTypeLabel[];
      cabins: CabinTypeLabel[];
      batteryCapacities: number[];
      engines: EngineTypeLabel[];
      weights: number[];
      drivetrains: DrivetrainLabel[];
      vehicles: TechDataVehicle[];
      variations: TechDataVariations;
    };
    translations: Record<string, string>;
    selectedVariant: TechDataVehicle;
  };
}>();

const country = ref('');
const language = ref('');
const stage = ref('');
const modelSeries = ref('');
const locale = ref('');
const sizes = ref<SizeTypeLabel[]>([]);
const roofs = ref<RoofTypeLabel[]>([]);
const cabins = ref<CabinTypeLabel[]>([]);
const engines = ref<EngineTypeLabel[]>([]);
const batteryCapacities = ref<number[]>([]);
const weights = ref<number[]>([]);
const drivetrains = ref<DrivetrainLabel[]>([]);
const variations = ref<TechDataVariations>();
const vehicles = ref<TechDataVehicle[]>([]);
const selected_variant = ref<TechDataVehicle>(props.data?.selectedVariant);
const translations = ref<Record<string, string>>({});
const appContainer = ref<HTMLElement | null>(null);

modelSeries.value = props.data?.aemData.modelSeries || '';
stage.value = props.data?.aemData.stage || '';
language.value = props.data?.aemData.language || '';
country.value = props.data?.aemData.country || '';
locale.value = `${props.data?.aemData.language}-${props.data?.aemData.country}`;
translations.value = props.data?.translations || {};
selected_variant.value = props.data?.selectedVariant;

const { trackImpression } = useTracking(modelSeries.value);

onMounted(async () => {
  const envData = await getPageEnvironmentVariables();

  if (!Object.keys(translations.value).length) {
    try {
      const data = await loadTranslations(envData.language, envData.country);
      translations.value = data ?? {};
    } catch (error) {
      console.error('Failed to load translations:', error);
      translations.value = {};
    }
  }

  if (!props.data.aemData) {
    modelSeries.value = envData.modelSeries;
    stage.value = envData.stage;
    language.value = envData.language;
    country.value = envData.country;
    locale.value = envData.locale;
  }

  if (props.data.selectedVariant) {
    selected_variant.value = props.data.selectedVariant;
  }

  nextTick(() => {
    observeComponent();
  });
});

sizes.value = props.data?.processedData.sizes ?? [];
roofs.value = props.data?.processedData.roofs ?? [];
cabins.value = props.data?.processedData.cabins ?? [];
engines.value = props.data?.processedData.engines ?? [];
batteryCapacities.value = props.data?.processedData.batteryCapacities ?? [];
weights.value = props.data?.processedData.weights ?? [];
drivetrains.value = props.data?.processedData.drivetrains ?? [];
vehicles.value = props.data?.processedData.vehicles ?? [];
variations.value = props.data?.processedData.variations;

const updateSelectedVariant = (e: TechDataVehicle) => {
  selected_variant.value = e;
};

const observeComponent = () => {
  if (!appContainer.value) return;

  // Check if the component is already in the viewport
  const rect = appContainer.value.getBoundingClientRect();
  const isAlreadyVisible = rect.top < window.innerHeight && rect.bottom > 0;

  if (isAlreadyVisible && vehicles.value.length > 0) {
    trackImpression(vehicles.value[0]);
    return;
  }

  const options = {
    root: null,
    rootMargin: '0px',
    threshold: [0.25], // Track when at least 25% is visible
  };

  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (entry.intersectionRatio >= 0.25 && vehicles.value.length > 0) {
        trackImpression(vehicles.value[0]);
        observer.disconnect(); // Stop observing after first trigger
      }
    });
  }, options);

  observer.observe(appContainer.value);
};

defineExpose({
  variations,
  vehicles,
  language,
  locale,
  modelSeries,
});
</script>

<template>
  <div id="owvans-app" class="owvans-tech-data" dir="ltr" ref="appContainer">
    <div v-if="variations?.variations.length && vehicles.length">
      <div class="wb-grid-container">
        <div class="owvans-tech-data-wrapper">
          <div>
            <tech-data-slider
              v-if="selected_variant"
              :images="selected_variant.imagesData"
              :modelSeries="modelSeries"
              :translations="translations"
              :vehicle="selected_variant"
              class="owvans-tech-data-wrapper__slider"
            />
            <tech-data-selector
              :sizes="sizes"
              :weights="weights"
              :roofs="roofs"
              :cabins="cabins"
              :drivetrains="drivetrains"
              :batteryCapacities="batteryCapacities"
              :engines="engines"
              :variations="variations"
              :vehicles="vehicles"
              :translations="translations"
              :selectedVariant="selected_variant"
              :modelSeries="modelSeries"
              class="owvans-tech-data__selector"
              @variant-selected="updateSelectedVariant($event)"
            />

            <tech-data-stats
              v-if="selected_variant"
              :locale="locale"
              :vehicle="selected_variant"
              :translations="translations"
              :modelSeries="modelSeries"
              class="owvans-tech-data__stats"
            />
          </div>
          <span class="wb-text-s">
            {{
              translations['owvc_tech_data_disclaimer'] ||
              getDefaultLocaleValue('disclaimer')
            }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import url('https://assets.oneweb.mercedes-benz.com/plugin/workbench/core/7.68.1/css/globals.css');
@import '@workbench/core/dist/scss/utility.scss';

.owvans-tech-data {
  @include wb-spacing('padding-bottom', 'xs');

  &__selector {
    @include wb-spacing('margin-top', 'm');
  }

  &__stats {
    @include wb-spacing('margin-top', 'm');
    @include wb-spacing('margin-bottom', 'xs');
  }
}
</style>
